import { DelopayError, DelopayAuthenticationError } from './error';
import { ApiKeys } from './resources/apiKeys';
import { Authentication } from './resources/authentication';
import { Billing } from './resources/billing';
import { Blocklist } from './resources/blocklist';
import { Connectors } from './resources/connectors';
import { Customers } from './resources/customers';
import { Disputes } from './resources/disputes';
import { PaypalDisputes } from './resources/paypalDisputes';
import { EphemeralKeys } from './resources/ephemeralKeys';
import { Events } from './resources/events';
import { Fees } from './resources/fees';
import { Mandates } from './resources/mandates';
import { MerchantAccounts } from './resources/merchantAccounts';
import { PaymentLinks } from './resources/paymentLinks';
import { PaymentMethods } from './resources/paymentMethods';
import { Payments } from './resources/payments';
import { Payouts } from './resources/payouts';
import { Poll } from './resources/poll';
import { ProfileAcquirers } from './resources/profileAcquirers';
import { Profiles } from './resources/profiles';
import { Projects } from './resources/projects';
import { Refunds } from './resources/refunds';
import { Relay } from './resources/relay';
import { Routing } from './resources/routing';
import { Search } from './resources/search';
import { Shops } from './resources/shops';
import { StripeConnect } from './resources/stripeConnect';
import { ThreeDsRules } from './resources/threeDsRules';
import { Users } from './resources/users';
import { Verification } from './resources/verification';
import { Webhooks } from './resources/webhooks';
import type { TokenResponse } from './types';
import { Analytics } from './resources/analytics';
import { AnalyticsDashboard } from './resources/analyticsDashboard';
import { Cards } from './resources/cards';
import { Export } from './resources/export';
import { FeatureMatrix } from './resources/featureMatrix';
import { Files } from './resources/files';
import { Forex } from './resources/forex';
import { Regions } from './resources/regions';
import { AvailabilityOverrides } from './resources/availabilityOverrides';
import { SellToRestrictions } from './resources/sellToRestrictions';
import { CostRules } from './resources/costRules';
import { ExternalRecords } from './resources/externalRecords';
import { Products } from './resources/products';
import { ProcessorCosts } from './resources/processorCosts';
import { CheckoutBrowserInfo } from './resources/checkoutBrowserInfo';
import { CheckoutMethods } from './resources/checkoutMethods';
import { Subscriptions } from './resources/subscriptions';
import { Audit } from './resources/audit';
import { Settlement } from './resources/settlement';
import { OperationLimits } from './resources/operationLimits';
import { Risk } from './resources/risk';

const PRODUCTION_URL = 'https://api.delopay.net';
const SANDBOX_URL = 'https://sandbox.delopay.net';

const MAX_RAW_BODY_BYTES = 2048;
const MAX_RETRY_AFTER_MS = 30_000;

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const trimmed = header.trim();
  const seconds = Number(trimmed);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
  }
  const date = Date.parse(trimmed);
  if (Number.isFinite(date)) {
    const delta = date - Date.now();
    return delta > 0 ? Math.min(delta, MAX_RETRY_AFTER_MS) : 0;
  }
  return null;
}

function truncateRawBody(raw: string): string | undefined {
  if (!raw) return undefined;
  return raw.length > MAX_RAW_BODY_BYTES ? raw.slice(0, MAX_RAW_BODY_BYTES) + '…' : raw;
}

function findIdempotencyKey(headers: Record<string, string>): string | undefined {
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === 'idempotency-key') return v;
  }
  return undefined;
}

interface CombinedSignal {
  signal: AbortSignal;
  dispose: () => void;
}

function noop(): void {
  // Intentionally empty: no listeners registered, nothing to clean up.
}

/**
 * A `onDownloadProgress` listener threw. Carried out of the transport as-is so
 * the retry logic never mistakes the caller's own failure — a `TypeError` in a
 * progress bar, say — for a network error and downloads the body again.
 */
class ListenerFailure {
  constructor(readonly cause: unknown) {}
}

/**
 * The body stream failed after the headers arrived: the connection dropped, or
 * the server aborted it because it could not finish the body it had started.
 * Surfaced as the transport's ordinary network error, so it is retried on
 * exactly the same terms as a failure before the first byte.
 */
class BodyStreamFailure {
  constructor(readonly cause: unknown) {}
}

function abortError(): Error {
  const error = new Error('The operation was aborted');
  error.name = 'AbortError';
  return error;
}

/** `Content-Length` as a byte count, or `null` when absent or unreadable. */
function parseContentLength(header: string | null): number | null {
  if (header === null || !/^\s*\d+\s*$/.test(header)) return null;
  const value = Number(header);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * One `reader.read()`, raced against the request's signal.
 *
 * A conforming `fetch` errors the body stream itself when its signal aborts,
 * but not every stream a runtime or a polyfill hands back does, and a read
 * that never settles would hold a timed-out or cancelled download open for
 * good. The race makes the abort reach the reader either way.
 */
function readChunk(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    reader.read().then(
      (result) => {
        signal.removeEventListener('abort', onAbort);
        resolve(result);
      },
      (cause: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(signal.aborted ? abortError() : new BodyStreamFailure(cause));
      },
    );
  });
}

/** The chunks as one buffer of exactly `length` bytes. */
function concatChunks(chunks: Uint8Array[], length: number): ArrayBuffer {
  const out = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out.buffer;
}

/**
 * Read a 2xx body chunk by chunk, reporting each one, and decode it as
 * `responseType` asks — the `onDownloadProgress` path of `request()`.
 *
 * `onActivity` is called when the headers arrive and on every chunk; the
 * caller uses it to restart its idle timer.
 */
async function receiveWithProgress(
  response: Response,
  responseType: RequestOptions['responseType'],
  listener: (event: DownloadProgressEvent) => void,
  attempt: number,
  signal: AbortSignal,
  onActivity: () => void,
): Promise<unknown> {
  const headers = response.headers ?? new Headers();
  const total = parseContentLength(headers.get('content-length'));
  let loaded = 0;
  const notify = (chunk: Uint8Array) => {
    try {
      // A copy: the request resolves to the bytes it read, whatever a
      // listener does with the ones it was shown.
      listener({ loaded, total, chunk: chunk.slice(), headers, attempt });
    } catch (cause) {
      throw new ListenerFailure(cause);
    }
  };

  onActivity();
  notify(new Uint8Array(0));

  const body = response.body;
  if (!body || typeof body.getReader !== 'function') {
    // No readable stream: a null-body status, or a runtime whose `fetch` does
    // not expose one. The body is read whole, and reported as one chunk.
    if (responseType === 'blob') {
      const blob = await response.blob();
      const bytes = new Uint8Array(await blob.arrayBuffer());
      loaded = bytes.byteLength;
      if (loaded > 0) notify(bytes);
      return blob;
    }
    if (responseType === 'arraybuffer') {
      const buffer = await response.arrayBuffer();
      loaded = buffer.byteLength;
      if (loaded > 0) notify(new Uint8Array(buffer));
      return buffer;
    }
    const text = await response.text();
    const bytes = new TextEncoder().encode(text);
    loaded = bytes.byteLength;
    if (loaded > 0) notify(bytes);
    return text ? JSON.parse(text) : undefined;
  }

  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  try {
    for (;;) {
      const { done, value } = await readChunk(reader, signal);
      if (done) break;
      if (!value || value.byteLength === 0) continue;
      chunks.push(value);
      loaded += value.byteLength;
      onActivity();
      notify(value);
    }
  } catch (err) {
    // Release the connection rather than leave a half-read body behind it.
    reader.cancel().catch(noop);
    throw err;
  }

  const bytes = concatChunks(chunks, loaded);
  if (responseType === 'blob') {
    return new Blob([bytes], { type: headers.get('content-type') ?? '' });
  }
  if (responseType === 'arraybuffer') {
    return bytes;
  }
  const text = new TextDecoder().decode(bytes);
  return text ? JSON.parse(text) : undefined;
}

function combineSignals(signals: AbortSignal[]): CombinedSignal {
  const controller = new AbortController();
  const listeners: { signal: AbortSignal; handler: () => void }[] = [];
  const dispose = () => {
    for (const { signal, handler } of listeners) {
      signal.removeEventListener('abort', handler);
    }
    listeners.length = 0;
  };
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      dispose();
      return { signal: controller.signal, dispose: noop };
    }
    const handler = () => {
      controller.abort(signal.reason);
      dispose();
    };
    signal.addEventListener('abort', handler, { once: true });
    listeners.push({ signal, handler });
  }
  return { signal: controller.signal, dispose };
}

/**
 * Events emitted by the debug logger.
 * - `request`  — about to send a request (`method`, `url`, `path`)
 * - `response` — response received (`status`, `method`, `path`, `requestId?`)
 * - `retry`   — about to retry after a transient failure (`attempt`, `maxRetries`, `method`, `path`)
 */
export type DelopayLogger = (
  event: 'request' | 'response' | 'retry',
  data: Record<string, unknown>,
) => void;

/**
 * Configuration options for the Delopay client.
 */
export interface DelopayOptions {
  /** Use the sandbox environment (`https://sandbox.delopay.net`). Defaults to `false` (production). */
  sandbox?: boolean;
  /** Override the base URL entirely. Takes precedence over `sandbox`. */
  baseUrl?: string;
  /** Request timeout in milliseconds. Defaults to `30000` (30 seconds). */
  timeout?: number;
  /**
   * Maximum number of automatic retries for transient failures (5xx, timeout, network errors).
   * Retries use exponential backoff. Set to `0` to disable. Defaults to `2`.
   * Only idempotent-safe requests (GET, DELETE, and requests with an `Idempotency-Key` header) are retried.
   */
  maxRetries?: number;
  /** Enable debug logging of requests and responses. Defaults to `false`. */
  debug?: boolean;
  /**
   * Custom logger for debug events (`request`, `response`, `retry`). When omitted,
   * debug output is written to `console.log`. Has no effect unless `debug` is `true`.
   * Useful for routing SDK logs through pino, winston, or similar structured loggers.
   */
  logger?: DelopayLogger;
}

const SENSITIVE_QUERY_KEYS = new Set([
  'client_secret',
  'ephemeral_key',
  'api_key',
  'publishable_key',
]);

/**
 * Return a copy of `url` with the values of known-sensitive query parameters
 * replaced by `REDACTED`, leaving the rest of the query string intact.
 * Used only to sanitize URLs before they hit debug logs.
 */
function redactUrlForLogging(url: string): string {
  const qIdx = url.indexOf('?');
  if (qIdx === -1) return url;
  const base = url.slice(0, qIdx);
  const query = url.slice(qIdx + 1);
  const parts = query.split('&').map((pair) => {
    const eqIdx = pair.indexOf('=');
    if (eqIdx === -1) return pair;
    const key = pair.slice(0, eqIdx);
    if (SENSITIVE_QUERY_KEYS.has(decodeURIComponent(key).toLowerCase())) {
      return `${key}=REDACTED`;
    }
    return pair;
  });
  return `${base}?${parts.join('&')}`;
}

/**
 * One step of a response body's download, reported to
 * {@link RequestOptions.onDownloadProgress}.
 */
export interface DownloadProgressEvent {
  /** Body bytes received so far on this attempt, `chunk` included. */
  loaded: number;
  /**
   * `Content-Length`, when the response carried one — a buffered body such as
   * a PDF. `null` for a streamed (chunked) body, whose size is not known
   * until it ends. With a compressed transfer it is the compressed size, so
   * `loaded` can pass it.
   */
  total: number | null;
  /**
   * The bytes this event reports. Empty on the first event of an attempt,
   * which fires as soon as the headers arrive, before any of the body.
   * A copy: changing it cannot change the value the request resolves to.
   */
  chunk: Uint8Array;
  /** The response headers, available from the first event on. */
  headers: Headers;
  /**
   * Which attempt this is, from `0`. A retried request reports its body
   * again from the start under the next number, so a listener that
   * accumulates state resets it when this changes.
   */
  attempt: number;
}

/**
 * Low-level options forwarded to a single HTTP request.
 */
export interface RequestOptions {
  /** Request body, serialised as JSON. */
  body?: unknown;
  /**
   * @internal Resource assertion that this operation deduplicates by the JSON
   * body's idempotency_key. Null means that optional key is absent, so the write
   * must not be retried. The transport checks the assertion against the body;
   * it does not send this option or copy a JSON key into an HTTP header.
   */
  bodyIdempotencyKey?: string | null;
  /**
   * Query-string parameters. `undefined` and `null` values are omitted.
   * Array values are emitted as repeated keys (`?tag=a&tag=b`) — not comma-joined.
   */
  query?: Record<
    string,
    string | number | boolean | null | undefined | (string | number | boolean)[]
  >;
  /** Additional HTTP headers merged with the default `api-key` header. */
  headers?: Record<string, string>;
  /**
   * Override the client-level timeout for this request, in milliseconds.
   *
   * Covers the whole request, body included — unless `onDownloadProgress` is
   * set, in which case it is an **idle** timeout: it restarts when the
   * headers arrive and on every chunk of the body, so a long download that
   * keeps making progress is never cut off, and one that stalls still is.
   */
  timeout?: number;
  /**
   * Caller-provided `AbortSignal`. Aborting it cancels the in-flight request and rejects
   * with a `DelopayError` carrying code `'ABORTED'`. Combined with the per-request timeout.
   */
  signal?: AbortSignal;
  /**
   * How to decode a 2xx response body. `'json'` (the default) parses JSON;
   * `'blob'` / `'arraybuffer'` return the raw bytes for binary endpoints
   * such as PDF exports. Error responses are always decoded as JSON and
   * thrown as `DelopayError` regardless of this setting.
   */
  responseType?: 'json' | 'blob' | 'arraybuffer';
  /**
   * Pass `keepalive: true` to let the request outlive its page — e.g.
   * telemetry sent while the document is navigating away. Browsers cap
   * keepalive request bodies at ~64 KiB and reject larger ones.
   */
  keepalive?: boolean;
  /**
   * Report a 2xx response body as it downloads. Called once as soon as the
   * headers arrive (with an empty `chunk` and `loaded: 0`), then once per
   * chunk of the body; the value the request resolves to is decoded from
   * those same bytes, per `responseType`. Error responses are not reported.
   *
   * Setting it turns `timeout` into an idle timeout (see there). A body that
   * fails part-way rejects with the ordinary `NETWORK` error and is retried
   * on the usual terms, starting again under the next `attempt`. A listener
   * that throws rejects the request with that error, unretried.
   */
  onDownloadProgress?: (event: DownloadProgressEvent) => void;
}

export type RequestFn = <T>(method: string, path: string, options?: RequestOptions) => Promise<T>;

/**
 * Per-call options that resource methods accept as an optional final argument:
 * extra HTTP headers (e.g. `Idempotency-Key`), a per-request timeout override,
 * an `AbortSignal` for cancellation, and a download-progress listener — the
 * last is what a file download (a statement PDF, a report CSV) reports its
 * bytes through.
 */
export type RequestExtras = Pick<
  RequestOptions,
  'headers' | 'timeout' | 'signal' | 'onDownloadProgress'
>;

/**
 * Delopay API client.
 *
 * Instantiate once with your API key and reuse across your application.
 * All resource sub-clients (payments, refunds, customers, …) are exposed
 * as properties on the instance.
 *
 * @example
 * ```typescript
 * const delopay = new Delopay('prd_...', { sandbox: false });
 * const payment = await delopay.payments.create({ amount: 5000, currency: 'EUR' });
 * ```
 */
export class Delopay {
  /** Utility for verifying incoming webhook signatures (static, no instance needed). */
  static webhooks = Webhooks;

  /** The resolved base URL used for all API requests. */
  readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly timeout: number;
  private readonly maxRetries: number;
  private readonly debug: boolean;
  private readonly logger?: DelopayLogger;
  private jwtToken?: string;

  // Merchant-facing
  readonly payments: Payments;
  readonly refunds: Refunds;
  readonly customers: Customers;
  readonly paymentMethods: PaymentMethods;
  readonly paymentLinks: PaymentLinks;
  readonly mandates: Mandates;
  readonly disputes: Disputes;
  readonly paypalDisputes: PaypalDisputes;
  readonly payouts: Payouts;
  readonly ephemeralKeys: EphemeralKeys;
  readonly events: Events;
  readonly poll: Poll;

  // Connector / routing
  readonly connectors: Connectors;
  readonly routing: Routing;
  readonly profiles: Profiles;
  readonly shops: Shops;
  readonly profileAcquirers: ProfileAcquirers;

  // Authentication & verification
  readonly authentication: Authentication;
  readonly verification: Verification;

  // Dashboard / internal
  readonly users: Users;
  readonly apiKeys: ApiKeys;
  readonly billing: Billing;
  readonly blocklist: Blocklist;
  readonly fees: Fees;
  readonly merchantAccounts: MerchantAccounts;
  readonly projects: Projects;
  readonly relay: Relay;
  readonly stripeConnect: StripeConnect;
  readonly threeDsRules: ThreeDsRules;
  /**
   * A merchant's own audit log. Needs a dashboard JWT, not an API key —
   * see {@link Audit}.
   */
  readonly audit: Audit;
  readonly settlement: Settlement;
  readonly operationLimits: OperationLimits;
  readonly risk: Risk;

  // New resources (Phases 3-4)
  readonly subscriptions: Subscriptions;
  readonly files: Files;
  readonly export: Export;
  readonly forex: Forex;
  readonly regions: Regions;
  readonly availabilityOverrides: AvailabilityOverrides;
  readonly sellToRestrictions: SellToRestrictions;
  readonly costRules: CostRules;

  /** Out-of-band transitions a PSP made that DeloPay never observed. */
  readonly externalRecords: ExternalRecords;

  /** A shop's product catalogue: what it sells, and at which prices. */
  readonly products: Products;
  /** What the merchant's own processor charges it — see {@link ProcessorCosts}. */
  readonly processorCosts: ProcessorCosts;
  readonly checkoutBrowserInfo: CheckoutBrowserInfo;
  readonly checkoutMethods: CheckoutMethods;
  readonly analytics: Analytics;
  readonly analyticsDashboard: AnalyticsDashboard;
  readonly featureMatrix: FeatureMatrix;
  readonly cards: Cards;
  readonly search: Search;

  /**
   * Create a new Delopay client.
   *
   * @param apiKey - Your Delopay API key (e.g. `prd_...` or `snd_...`).
   *   Pass an empty string or omit for JWT-only usage (e.g. dashboard apps).
   * @param options - Optional configuration (sandbox mode, base URL override, timeout).
   */
  constructor(apiKey?: string, options?: DelopayOptions) {
    this.apiKey = apiKey ?? '';
    this.timeout = options?.timeout ?? 30_000;
    this.maxRetries = options?.maxRetries ?? 2;
    this.debug = options?.debug ?? false;
    if (options?.logger !== undefined) this.logger = options.logger;

    if (options?.baseUrl !== undefined) {
      this.baseUrl = options.baseUrl;
    } else if (options?.sandbox) {
      this.baseUrl = SANDBOX_URL;
    } else {
      this.baseUrl = PRODUCTION_URL;
    }

    const request = this.request.bind(this) as RequestFn;

    // JWT auth methods are defined below (setJwtToken / clearJwtToken)

    // Merchant-facing
    this.payments = new Payments(request);
    this.refunds = new Refunds(request);
    this.customers = new Customers(request);
    this.paymentMethods = new PaymentMethods(request);
    this.paymentLinks = new PaymentLinks(request);
    this.mandates = new Mandates(request);
    this.disputes = new Disputes(request);
    this.paypalDisputes = new PaypalDisputes(request);
    this.payouts = new Payouts(request);
    this.ephemeralKeys = new EphemeralKeys(request);
    this.events = new Events(request);
    this.poll = new Poll(request);

    // Connector / routing
    this.connectors = new Connectors(request);
    this.routing = new Routing(request);
    this.profiles = new Profiles(request);
    this.shops = new Shops(request);
    this.profileAcquirers = new ProfileAcquirers(request);

    // Authentication & verification
    this.authentication = new Authentication(request);
    this.verification = new Verification(request);

    // Dashboard / internal
    this.users = new Users(request);
    this.apiKeys = new ApiKeys(request);
    this.billing = new Billing(request);
    this.blocklist = new Blocklist(request);
    this.fees = new Fees(request);
    this.merchantAccounts = new MerchantAccounts(request);
    this.projects = new Projects(request);
    this.relay = new Relay(request);
    this.stripeConnect = new StripeConnect(request);
    this.threeDsRules = new ThreeDsRules(request);
    this.audit = new Audit(request);
    this.settlement = new Settlement(request);
    this.operationLimits = new OperationLimits(request);
    this.risk = new Risk(request);

    // New resources (Phases 3-4)
    this.subscriptions = new Subscriptions(request);
    this.files = new Files(request);
    this.export = new Export(request);
    this.forex = new Forex(request);
    this.regions = new Regions(request);
    this.availabilityOverrides = new AvailabilityOverrides(request);
    this.sellToRestrictions = new SellToRestrictions(request);
    this.costRules = new CostRules(request);
    this.externalRecords = new ExternalRecords(request);
    this.products = new Products(request);
    this.processorCosts = new ProcessorCosts(request);
    this.checkoutBrowserInfo = new CheckoutBrowserInfo(request);
    this.checkoutMethods = new CheckoutMethods(request);
    this.analytics = new Analytics(request);
    this.analyticsDashboard = new AnalyticsDashboard(request);
    this.featureMatrix = new FeatureMatrix(request);
    this.cards = new Cards(request);
    this.search = new Search(request);
  }

  /**
   * Set a JWT token for subsequent requests.
   * When set, requests use `Authorization: Bearer <token>` instead of `api-key`.
   * Useful after `users.signIn()` returns a JWT for dashboard operations.
   */
  setJwtToken(token: string): void {
    this.jwtToken = token;
  }

  /**
   * Clear the JWT token, reverting to API key authentication.
   */
  clearJwtToken(): void {
    this.jwtToken = undefined;
  }

  /**
   * Refresh the current login JWT (see {@link Users.refreshToken}) and
   * apply the fresh token to this client, so subsequent requests use it.
   * Returns the fresh token for the caller to persist (e.g. session
   * storage) — the backend has already re-set the `login_token` cookie.
   *
   * If the client's auth state changes while the refresh is pending —
   * `clearJwtToken()` on sign-out, or `setJwtToken()` switching to another
   * session — the stale completion is discarded and this rejects with a
   * `session_changed` `DelopayError` (status 0), so the explicit change
   * wins and the caller never persists a token for a session that is gone.
   *
   * Otherwise throws like any other request; in particular a 401 means the
   * session is dead (expired/blacklisted/revoked), a 429 means a refresh
   * was already minted for this session within the last minute, and a 400
   * means this token has no revocable session to slide (no `jti` — team
   * impersonation is the case in practice) and can never be refreshed,
   * though it stays valid for ordinary calls.
   */
  async refreshSession(): Promise<TokenResponse> {
    const originatingToken = this.jwtToken;
    const response = await this.users.refreshToken();
    if (this.jwtToken !== originatingToken) {
      throw new DelopayError(
        'Auth state changed while the refresh was pending; refreshed token discarded',
        {
          status: 0,
          code: 'session_changed',
          type: 'session_changed',
        },
      );
    }
    this.setJwtToken(response.token);
    return response;
  }

  /**
   * Make a raw HTTP request to the Delopay API.
   *
   * You rarely need to call this directly — prefer the typed resource methods.
   * Use it only for endpoints not yet covered by a resource class.
   *
   * @param method - HTTP method (`GET`, `POST`, `PUT`, `PATCH`, `DELETE`).
   * @param path - API path starting with `/` (e.g. `/payments`).
   * @param options - Optional body, query parameters, and headers.
   * @returns Parsed JSON response body typed as `T`.
   * @throws {DelopayAuthenticationError} On 401 responses.
   * @throws {DelopayError} On all other non-2xx responses, timeouts, and network errors.
   */
  async request<T>(method: string, path: string, options?: RequestOptions): Promise<T> {
    let url = `${this.baseUrl}${path}`;

    if (options?.query) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(options.query)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
          for (const v of value) {
            if (v !== undefined && v !== null) params.append(key, String(v));
          }
        } else {
          params.set(key, String(value));
        }
      }
      const qs = params.toString();
      if (qs) {
        url += `?${qs}`;
      }
    }

    let headers: Record<string, string> = {
      ...(this.jwtToken
        ? { Authorization: `Bearer ${this.jwtToken}` }
        : this.apiKey
          ? { 'api-key': this.apiKey }
          : {}),
      ...options?.headers,
    };

    // FormData / Blob / ArrayBuffer / URLSearchParams pass through unchanged so callers
    // can send multipart uploads. The runtime (browser or Node 18+ fetch) sets the
    // appropriate Content-Type including the multipart boundary, so we don't touch it.
    const isRawBody =
      options?.body !== undefined &&
      options?.body !== null &&
      (options.body instanceof FormData ||
        options.body instanceof Blob ||
        options.body instanceof ArrayBuffer ||
        options.body instanceof URLSearchParams);

    if (options?.body && !isRawBody) {
      headers['Content-Type'] = 'application/json';
    }

    // Serialize body once so circular-reference errors surface immediately and big payloads
    // aren't re-stringified on every retry attempt. Raw bodies pass through untouched.
    let serializedBody: BodyInit | undefined;
    if (isRawBody) {
      serializedBody = options?.body as BodyInit;
    } else if (options?.body) {
      serializedBody = JSON.stringify(options.body);
    }

    let idempotencyKey = findIdempotencyKey(headers)?.trim();
    if (options && Object.prototype.hasOwnProperty.call(options, 'bodyIdempotencyKey')) {
      const bodyKey = options?.bodyIdempotencyKey;
      const serialized: unknown =
        !isRawBody && typeof serializedBody === 'string' ? JSON.parse(serializedBody) : null;
      const actualKey =
        serialized && typeof serialized === 'object' && !Array.isArray(serialized)
          ? ((serialized as Record<string, unknown>).idempotency_key ?? null)
          : null;
      const suppliedHeaders = Object.keys(headers).filter(
        (name) => name.toLowerCase() === 'idempotency-key',
      );
      if (
        (bodyKey !== null && typeof bodyKey !== 'string') ||
        actualKey !== bodyKey ||
        suppliedHeaders.some(
          (name) => headers[name] !== bodyKey || headers[name]?.trim() !== headers[name],
        )
      ) {
        throw new DelopayError('Request retry keys must match the JSON body key', {
          status: 0,
          code: 'IDEMPOTENCY_KEY_MISMATCH',
          type: 'invalid_request',
        });
      }
      idempotencyKey = bodyKey?.trim();
      // Collapse differently cased copies so fetch cannot combine them into a
      // different header value. Other caller headers remain untouched.
      if (suppliedHeaders.length > 0) {
        headers = {
          ...Object.fromEntries(
            Object.entries(headers).filter(([name]) => name.toLowerCase() !== 'idempotency-key'),
          ),
          'Idempotency-Key': idempotencyKey ?? '',
        };
      }
    }
    const isRetryable =
      method === 'GET' ||
      method === 'DELETE' ||
      (idempotencyKey !== undefined && idempotencyKey !== '');

    const callerSignal = options?.signal;
    if (callerSignal?.aborted) {
      throw new DelopayError('Request aborted', {
        status: 0,
        code: 'ABORTED',
        type: 'abort_error',
      });
    }

    const timeoutMs = options?.timeout ?? this.timeout;

    let lastError: unknown;
    let retryAfterOverrideMs: number | null = null;

    const safeUrl = () => redactUrlForLogging(url);
    const emit = (event: 'request' | 'response' | 'retry', data: Record<string, unknown>) => {
      if (!this.debug) return;
      if (this.logger) {
        this.logger(event, data);
        return;
      }
      if (event === 'request')
        console.log(`[delopay] ${data.method as string} ${data.url as string}`);
      else if (event === 'response')
        console.log(
          `[delopay] ${data.status as number} ${data.method as string} ${data.path as string}`,
        );
      else
        console.log(
          `[delopay] retry ${data.attempt as number}/${data.maxRetries as number} ${data.method as string} ${data.path as string}`,
        );
    };

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      if (attempt > 0) {
        const base = Math.min(500 * 2 ** (attempt - 1), 5000);
        // Full jitter: pick uniformly in [0, base) to avoid synchronized retry storms.
        const jittered = Math.random() * base;
        const delay = Math.max(retryAfterOverrideMs ?? 0, jittered);
        retryAfterOverrideMs = null;
        await new Promise((resolve) => setTimeout(resolve, delay));
        emit('retry', { attempt, maxRetries: this.maxRetries, method, path });
      }

      const timeoutCtrl = new AbortController();
      let timeoutId = setTimeout(() => timeoutCtrl.abort(), timeoutMs);
      // With a progress listener the timeout measures silence, not the whole
      // download: each sign of life restarts it.
      const restartIdleTimer = () => {
        if (timeoutCtrl.signal.aborted) return;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => timeoutCtrl.abort(), timeoutMs);
      };
      const combined = combineSignals(
        callerSignal ? [timeoutCtrl.signal, callerSignal] : [timeoutCtrl.signal],
      );

      try {
        emit('request', { method, url: safeUrl(), path });

        const response = await fetch(url, {
          method,
          headers,
          body: serializedBody,
          signal: combined.signal,
          ...(options?.keepalive !== undefined ? { keepalive: options.keepalive } : {}),
        });

        const requestId =
          response.headers?.get('x-request-id') ?? response.headers?.get('x-trace-id') ?? undefined;

        emit('response', { status: response.status, method, path, requestId });

        if (!response.ok) {
          const rawBody = await response.text().catch(() => '');
          let parsed: Record<string, unknown> = {};
          if (rawBody) {
            try {
              parsed = JSON.parse(rawBody) as Record<string, unknown>;
            } catch {
              // non-JSON error body (HTML from a proxy, truncated stream, etc.) — keep raw
            }
          }
          // API wraps errors as { error: { message, code, type, data? } } — unwrap if present
          const err = (parsed.error as Record<string, unknown>) ?? parsed;
          const message =
            (err.message as string) ?? `Request failed with status ${response.status}`;
          const code = (err.code as string) ?? '';
          const type = (err.error_type as string) ?? (err.type as string) ?? '';
          const data =
            err.data && typeof err.data === 'object' && !Array.isArray(err.data)
              ? (err.data as Record<string, unknown>)
              : undefined;
          const truncatedRaw = truncateRawBody(rawBody);

          // A 401 usually means OUR credential (API key / JWT) was rejected —
          // but connector-passthrough errors (`CE_*` / type "connector", e.g.
          // a PSP rejecting the merchant's stored Stripe key) propagate the
          // upstream status and are NOT a Delopay authentication failure, so
          // they stay a plain DelopayError instead of triggering the
          // "re-authenticate" handling dashboards attach to auth errors.
          const isConnectorPassthrough = type === 'connector' || code.startsWith('CE_');
          if (response.status === 401 && !isConnectorPassthrough) {
            throw new DelopayAuthenticationError(message, {
              code,
              type,
              requestId,
              rawBody: truncatedRaw,
              data,
            });
          }

          const error = new DelopayError(message, {
            status: response.status,
            code,
            type,
            requestId,
            rawBody: truncatedRaw,
            data,
          });

          // Retry on 5xx and 429 (rate limit). 4xx other than 429 are not transient.
          const isTransientStatus = response.status >= 500 || response.status === 429;
          if (isTransientStatus && isRetryable && attempt < this.maxRetries) {
            if (response.status === 429) {
              retryAfterOverrideMs = parseRetryAfter(response.headers?.get('retry-after') ?? null);
            }
            lastError = error;
            continue;
          }

          throw error;
        }

        if (options?.onDownloadProgress) {
          return (await receiveWithProgress(
            response,
            options.responseType,
            options.onDownloadProgress,
            attempt,
            combined.signal,
            restartIdleTimer,
          )) as T;
        }
        if (options?.responseType === 'blob') {
          return (await response.blob()) as T;
        }
        if (options?.responseType === 'arraybuffer') {
          return (await response.arrayBuffer()) as T;
        }
        const text = await response.text();
        return (text ? JSON.parse(text) : undefined) as T;
      } catch (err) {
        if (err instanceof DelopayError || err instanceof DelopayAuthenticationError) {
          throw err;
        }
        if (err instanceof ListenerFailure) {
          throw err.cause;
        }
        if (err instanceof BodyStreamFailure) {
          const cause = err.cause;
          const detail =
            cause instanceof Error ? cause.message : String(cause ?? 'body stream failed');
          lastError = new DelopayError(`Network error: ${detail}`, {
            status: 0,
            code: 'NETWORK',
            type: 'network_error',
          });
          if (isRetryable && attempt < this.maxRetries) continue;
          throw lastError;
        }
        if (err instanceof Error && err.name === 'AbortError') {
          if (callerSignal?.aborted) {
            throw new DelopayError('Request aborted', {
              status: 0,
              code: 'ABORTED',
              type: 'abort_error',
            });
          }
          lastError = new DelopayError('Request timed out', {
            status: 0,
            code: 'TIMEOUT',
            type: 'timeout_error',
          });
          if (isRetryable && attempt < this.maxRetries) continue;
          throw lastError;
        }
        if (err instanceof TypeError) {
          lastError = new DelopayError(`Network error: ${err.message}`, {
            status: 0,
            code: 'NETWORK',
            type: 'network_error',
          });
          if (isRetryable && attempt < this.maxRetries) continue;
          throw lastError;
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
        combined.dispose();
      }
    }

    throw lastError;
  }

  /**
   * Auto-paginate a list endpoint. Yields items one by one, fetching
   * the next page automatically when the current one is exhausted.
   *
   * Delopay list endpoints use one of two pagination styles, so this helper
   * supports both:
   * - **Offset** (default) — for endpoints like `customers.list` that accept
   *   `offset`/`limit`. Each page advances `offset` by the number of items returned.
   * - **Cursor** — for endpoints like `payments.list` and `payouts.list` that page
   *   with `starting_after`/`limit` (they ignore `offset`). Pass a `cursor` extractor
   *   that returns the id of an item; the next page is requested with
   *   `starting_after` set to the last item's id.
   *
   * @param listFn - A function that takes the paging params and returns `{ data: T[] }` or `T[]`.
   * @param params - Additional parameters to pass to every page request.
   * @param options - Page size (number) for offset mode, or `{ pageSize?, cursor? }`.
   *   Provide `cursor` to switch to cursor pagination.
   *
   * @example
   * ```typescript
   * // Offset endpoint (customers):
   * for await (const c of delopay.paginate((p) => delopay.customers.list(p))) {
   *   console.log(c.customer_id);
   * }
   *
   * // Cursor endpoint (payments): extract the id used as the next cursor.
   * for await (const payment of delopay.paginate(
   *   (p) => delopay.payments.list(p),
   *   undefined,
   *   { cursor: (p) => p.payment_id },
   * )) {
   *   console.log(payment.payment_id);
   * }
   * ```
   */
  async *paginate<T, P extends Record<string, unknown>>(
    listFn: (
      params: P & { limit: number; offset?: number; starting_after?: string },
    ) => Promise<{ data: T[] } | T[]>,
    params?: P,
    options?: number | { pageSize?: number; cursor?: (item: T) => string | undefined },
  ): AsyncGenerator<T> {
    const pageSize = typeof options === 'number' ? options : (options?.pageSize ?? 50);
    const cursorOf = typeof options === 'object' ? options.cursor : undefined;
    let offset = 0;
    let after: string | undefined;
    while (true) {
      const page = { ...((params ?? {}) as P), limit: pageSize } as P & {
        limit: number;
        offset?: number;
        starting_after?: string;
      };
      if (cursorOf) {
        if (after !== undefined) page.starting_after = after;
      } else {
        page.offset = offset;
      }
      const result = await listFn(page);
      const items = Array.isArray(result) ? result : result.data;
      if (items.length === 0) break;
      for (const item of items) {
        yield item;
      }
      if (items.length < pageSize) break;
      if (cursorOf) {
        const last = items[items.length - 1];
        if (last === undefined) break;
        after = cursorOf(last);
        if (after === undefined) break;
      } else {
        offset += items.length;
      }
    }
  }
}
